import {
  ClipboardCopy,
  KeyRound,
  LoaderCircle,
  Mail,
  Plus,
  ShieldCheck,
  Users,
} from "lucide-react";
import React from "react";
import { getApiErrorMessage, portalApi } from "../api/client";
import type {
  ApiEnvelope,
  PortalUser,
  PortalUserLink,
  PortalUserStatus,
} from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { PageError } from "../components/PageError";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui";

type PageState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; users: PortalUser[] };

const statusView: Record<
  PortalUserStatus,
  { label: string; className: string }
> = {
  active: {
    label: "Ativo",
    className: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
  },
  invited: {
    label: "Convite pendente",
    className: "bg-amber-100 text-amber-800 hover:bg-amber-100",
  },
  disabled: {
    label: "Desativado",
    className: "bg-neutral-100 text-neutral-700 hover:bg-neutral-100",
  },
};

export function UsersPage() {
  const auth = useAuth();
  const [state, setState] = React.useState<PageState>({ status: "loading" });
  const [createOpen, setCreateOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [actionUserId, setActionUserId] = React.useState<number | null>(null);
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [role, setRole] = React.useState("2");
  const [generatedLink, setGeneratedLink] = React.useState("");
  const [linkTitle, setLinkTitle] = React.useState("Link gerado");
  const [actionError, setActionError] = React.useState("");
  const [linkCopied, setLinkCopied] = React.useState(false);

  const load = React.useCallback(async () => {
    setState({ status: "loading" });
    try {
      const response = await portalApi.get<ApiEnvelope<PortalUser[]>>(
        "/portal/users"
      );
      setState({ status: "ready", users: response.data.data || [] });
    } catch (error) {
      setState({
        status: "error",
        message: getApiErrorMessage(
          error,
          "Não foi possível carregar os usuários do portal."
        ),
      });
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  function openCreate() {
    setName("");
    setEmail("");
    setRole("2");
    setActionError("");
    setCreateOpen(true);
  }

  async function createUser() {
    if (!name.trim() || !email.trim()) return;
    setSaving(true);
    setActionError("");
    try {
      const response = await portalApi.post<ApiEnvelope<PortalUserLink>>(
        "/portal/users",
        { name: name.trim(), email: email.trim(), role: Number(role) }
      );
      setCreateOpen(false);
      setLinkTitle("Link de convite gerado");
      setGeneratedLink(response.data.data.link);
      setLinkCopied(false);
      await load();
    } catch (error) {
      setActionError(
        getApiErrorMessage(error, "Não foi possível convidar o usuário.")
      );
    } finally {
      setSaving(false);
    }
  }

  async function updateUser(
    user: PortalUser,
    patch: { role?: number; enabled?: number }
  ) {
    setActionUserId(user.cdsupplieruser);
    setActionError("");
    try {
      await portalApi.patch(`/portal/users/${user.cdsupplieruser}`, patch);
      await load();
    } catch (error) {
      setActionError(
        getApiErrorMessage(error, "Não foi possível atualizar o acesso.")
      );
    } finally {
      setActionUserId(null);
    }
  }

  async function issueLink(
    user: PortalUser,
    purpose: "reinvite" | "reset"
  ) {
    setActionUserId(user.cdsupplieruser);
    setActionError("");
    try {
      const response = await portalApi.post<ApiEnvelope<PortalUserLink>>(
        `/portal/users/${user.cdsupplieruser}/${purpose}`
      );
      setLinkTitle(
        purpose === "reinvite"
          ? "Novo link de convite"
          : "Link de redefinição de senha"
      );
      setGeneratedLink(response.data.data.link);
      setLinkCopied(false);
      if (purpose === "reinvite") await load();
    } catch (error) {
      setActionError(getApiErrorMessage(error, "Não foi possível gerar o link."));
    } finally {
      setActionUserId(null);
    }
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setLinkCopied(true);
    } catch {
      setActionError(
        "Não foi possível copiar o link. Selecione e copie-o manualmente."
      );
    }
  }

  if (state.status === "loading") {
    return (
      <div className="space-y-5" aria-label="Carregando usuários">
        <div className="h-20 w-80 animate-pulse rounded-xl bg-slate-200" />
        <div className="h-64 animate-pulse rounded-2xl bg-slate-200" />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <PageError
        title="Não foi possível abrir os usuários"
        description={state.message}
        onRetry={load}
      />
    );
  }

  const currentUserId = auth.me!.supplier.supplierUserId;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-teal-700">Administração</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950">
            Usuários do portal
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
            Convide colegas da sua empresa e defina quem administra os acessos
            ou atua como colaborador.
          </p>
        </div>
        <Button type="button" onClick={openCreate}>
          <Plus className="h-4 w-4" /> Adicionar usuário
        </Button>
      </header>

      {actionError && (
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {actionError}
        </div>
      )}

      <section className="overflow-hidden rounded-lg border bg-white">
        {state.users.length === 0 ? (
          <div className="p-10 text-center">
            <Users className="mx-auto text-slate-400" size={30} />
            <h2 className="mt-3 font-semibold text-slate-900">
              Nenhum usuário encontrado
            </h2>
          </div>
        ) : (
          <div className="divide-y">
            {state.users.map((user) => {
              const ownAccess = user.cdsupplieruser === currentUserId;
              const busy = actionUserId === user.cdsupplieruser;
              const enabled = Number(user.fgenabled) === 1;
              const status = statusView[user.idstatus];
              return (
                <article
                  key={user.cdsupplieruser}
                  className="grid gap-4 p-4 xl:grid-cols-[minmax(220px,1fr)_180px_auto] xl:items-center"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate font-medium text-slate-900">
                        {user.nmuser}
                      </p>
                      {ownAccess && (
                        <Badge variant="outline">
                          Você
                        </Badge>
                      )}
                      <Badge className={status.className}>
                        {status.label}
                      </Badge>
                    </div>
                    <p className="truncate text-sm text-slate-500">
                      {user.nmemail}
                    </p>
                  </div>

                  <Select
                    value={String(user.fgrole)}
                    onValueChange={(value) =>
                      void updateUser(user, { role: Number(value) })
                    }
                    disabled={ownAccess || busy || !enabled}
                  >
                    <SelectTrigger aria-label={`Papel de ${user.nmuser}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Administrador</SelectItem>
                      <SelectItem value="2">Colaborador</SelectItem>
                    </SelectContent>
                  </Select>

                  <div className="flex flex-wrap gap-2 lg:justify-end">
                    {!ownAccess && user.idstatus === "invited" && enabled && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        onClick={() => void issueLink(user, "reinvite")}
                      >
                        <Mail size={15} /> Reenviar convite
                      </Button>
                    )}
                    {!ownAccess && user.idstatus === "active" && enabled && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        onClick={() => void issueLink(user, "reset")}
                      >
                        <KeyRound size={15} /> Redefinir senha
                      </Button>
                    )}
                    {!ownAccess && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        onClick={() =>
                          void updateUser(user, { enabled: enabled ? 2 : 1 })
                        }
                      >
                        {busy && <LoaderCircle className="animate-spin" size={15} />}
                        {enabled ? "Desativar" : "Ativar"}
                      </Button>
                    )}
                    {ownAccess && (
                      <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                        <ShieldCheck size={15} /> Acesso atual
                      </span>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <p className="text-xs text-muted-foreground">
        Papéis disponíveis: Administrador e Colaborador. O envio automático ainda não está configurado; compartilhe manualmente os links gerados.
      </p>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convidar usuário</DialogTitle>
            <DialogDescription>
              Será gerado um link para a pessoa confirmar o acesso a esta empresa.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            {actionError && (
              <div
                role="alert"
                className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                {actionError}
              </div>
            )}
            <div className="grid gap-2">
              <Label htmlFor="portal-colleague-name">Nome</Label>
              <Input
                id="portal-colleague-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                maxLength={255}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="portal-colleague-email">E-mail</Label>
              <Input
                id="portal-colleague-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                maxLength={255}
              />
            </div>
            <div className="grid gap-2">
              <Label>Papel</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Administrador</SelectItem>
                  <SelectItem value="2">Colaborador</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Administradores gerenciam os acessos. Colaboradores trabalham
                com a documentação e os ativos.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={() => void createUser()}
              disabled={saving || !name.trim() || !email.trim()}
            >
              {saving && <LoaderCircle className="animate-spin" size={16} />}
              {saving ? "Gerando convite..." : "Convidar e gerar link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(generatedLink)}
        onOpenChange={(open) => !open && setGeneratedLink("")}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{linkTitle}</DialogTitle>
            <DialogDescription>
              Copie este endereço e envie-o à pessoa. Um novo link invalida o
              anterior da mesma finalidade.
            </DialogDescription>
          </DialogHeader>
          {actionError && (
            <div
              role="alert"
              className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {actionError}
            </div>
          )}
          <div className="flex flex-col gap-2 py-2 sm:flex-row">
            <Input value={generatedLink} readOnly aria-label={linkTitle} />
            <Button type="button" variant="outline" onClick={() => void copyLink()}>
              <ClipboardCopy size={16} /> {linkCopied ? "Copiado" : "Copiar"}
            </Button>
          </div>
          <DialogFooter>
            <Button type="button" onClick={() => setGeneratedLink("")}>
              Concluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
